import { useEffect, useRef, useState } from 'react';
import { Editor } from '@tiptap/react';

import { useKeyPress } from '@/lib/hooks/use-key-press';
import { Icon } from '@/components/icon';
import { Tooltip } from '@/components/tooltip';

import styles from '../text-cell.module.scss';

interface VideoMenuProps {
  editor: Editor;
  hideVideoMenu: () => void;
  initialHref?: string;
}

export const VideoMenu = ({ editor, initialHref, hideVideoMenu }: VideoMenuProps) => {
  const [videoUrl, setVideoUrl] = useState<string>('');
  const urlInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialHref !== undefined) {
      setVideoUrl(initialHref);
    }
    urlInputRef.current?.focus();
  }, [initialHref]);

  useKeyPress(
    'escape',
    (event) => {
      event.stopPropagation();
      setVideoUrl('');
      hideVideoMenu();
    },
    { capture: true, includeInputs: true },
  );

  if (editor === null) {
    return <></>;
  }

  const handleUrlInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    addVideo();
  };

  const addVideo = () => {
    if (videoUrl === '') {
      return;
    }

    const href =
      videoUrl.startsWith('http://') || videoUrl.startsWith('https://')
        ? videoUrl
        : `https://${videoUrl}`;

    editor.commands.addVideo(href);
    setVideoUrl('');
    hideVideoMenu();
  };

  const handleBackClick = () => {
    setVideoUrl('');
    hideVideoMenu();
  };

  return (
    <div className={styles.popupMenu}>
      <Tooltip content="Back">
        <button onMouseDown={(e) => e.preventDefault()} onClick={handleBackClick}>
          <Icon name="ArrowLeft" size={16} />
        </button>
      </Tooltip>
      <input
        type="text"
        value={videoUrl}
        onChange={(event) => setVideoUrl(event.target.value)}
        placeholder="Enter video URL"
        onKeyDown={handleUrlInputKeyDown}
        className={styles.urlInput}
        ref={urlInputRef}
      />
      <div className={styles.separator} />
      <Tooltip content="Insert video via URL">
        <button onMouseDown={(e) => e.preventDefault()} onClick={addVideo}>
          <Icon name="CornerDownLeft" size={16} />
        </button>
      </Tooltip>
    </div>
  );
};
